import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Scale, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { firmInfo, services } from '../mock';

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1687289133469-b2a07a13b78b"
            alt="Scales of Justice"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-navy-900/95 via-navy-800/90 to-navy-900/95"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-gold-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-navy-600/20 rounded-full blur-3xl"></div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 lg:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-gold-400/20 backdrop-blur-sm rounded-full">
              <p className="text-gold-300 text-sm font-semibold">Trusted Immigration Legal Services • Fluent in Spanish & English</p>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              Your Path to the American Dream
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto">
              {firmInfo.tagline} — Expert legal representation with compassion and dedication for all your immigration needs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-navy-900 px-8 py-6 text-lg font-bold rounded-xl shadow-2xl hover:shadow-gold-500/50 transition-all duration-300 hover:scale-105">
                  Schedule Consultation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-navy-900 px-8 py-6 text-lg font-bold rounded-xl transition-all duration-300 hover:scale-105">
                  View Our Services
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-gold-400" />
                <span className="text-sm font-medium">25+ Years Experience</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-gold-400" />
                <span className="text-sm font-medium">Fluent in Spanish</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-gold-400" />
                <span className="text-sm font-medium">500+ Cases Resolved</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-gold-400 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-gold-400 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">
              Our Immigration Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive legal solutions tailored to your unique immigration needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.slice(0, 3).map((service, index) => (
              <div
                key={service.id}
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <h3 className="text-xl font-bold text-white">{service.title}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                  <Link
                    to="/services"
                    className="inline-flex items-center text-gold-600 font-semibold hover:text-gold-700 transition-colors duration-200"
                  >
                    Learn More <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link to="/services">
              <Button className="bg-navy-800 hover:bg-navy-900 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-6">
                Why Choose Our Firm?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {firmInfo.description}
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Fluent in Spanish & English - Hablo Español',
                  'Personalized attention to every case',
                  'Transparent communication throughout',
                  'Proven track record of success',
                  'Compassionate and understanding approach'
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-gold-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about">
                <Button className="bg-gradient-to-r from-navy-700 to-navy-900 hover:from-navy-800 hover:to-navy-950 text-white px-8 py-6 text-lg font-semibold rounded-xl shadow-lg transition-all duration-300">
                  Learn More About Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src="/gabriela-photo.jpg"
                alt="Attorney Gabriela Mendoza Thibeau"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold-400 text-navy-900 p-6 rounded-xl shadow-xl">
                <p className="text-4xl font-bold">25+</p>
                <p className="text-sm font-semibold">Years Experience</p>
              </div>
            </div>
          </div>
          
          {/* Credentials Bar */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <p className="text-center text-sm font-semibold text-navy-800 mb-6 uppercase tracking-wide">
              Licensed & Admitted to Practice
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 mb-8">
              <div className="flex items-center space-x-2 text-navy-700">
                <Scale className="w-5 h-5 text-gold-600" />
                <span className="font-medium">State of Ohio</span>
              </div>
              <div className="w-1 h-6 bg-gold-400"></div>
              <div className="flex items-center space-x-2 text-navy-700">
                <Scale className="w-5 h-5 text-gold-600" />
                <span className="font-medium">Commonwealth of Massachusetts</span>
              </div>
              <div className="w-1 h-6 bg-gold-400"></div>
              <div className="flex items-center space-x-2 text-navy-700">
                <Scale className="w-5 h-5 text-gold-600" />
                <span className="font-medium">U.S. Supreme Court</span>
              </div>
            </div>

            <p className="text-center text-sm font-semibold text-navy-800 mb-6 uppercase tracking-wide">
              Professional Associations
            </p>
            <div className="flex flex-wrap justify-center items-center gap-6">
              <div className="flex items-center space-x-2 text-navy-700">
                <Award className="w-5 h-5 text-gold-600" />
                <span className="font-medium text-sm">Butler County Bar Association</span>
              </div>
              <div className="w-1 h-6 bg-navy-300"></div>
              <div className="flex items-center space-x-2 text-navy-700">
                <Award className="w-5 h-5 text-gold-600" />
                <span className="font-medium text-sm">Cincinnati Bar Association</span>
              </div>
              <div className="w-1 h-6 bg-navy-300"></div>
              <div className="flex items-center space-x-2 text-navy-700">
                <Award className="w-5 h-5 text-gold-600" />
                <span className="font-medium text-sm">American Immigration Lawyers Association</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Reviews Section */}
      <section className="py-20 bg-gradient-to-br from-navy-50 to-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Read authentic reviews from clients we've helped achieve their immigration goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Google Reviews */}
            <a
              href="https://g.page/r/CYt8TYXxDplgEAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
            >
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-3 text-center">
                Google Reviews
              </h3>
              <p className="text-gray-600 text-center mb-4">
                See what our clients say about their experience working with us
              </p>
              <div className="flex items-center justify-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-center text-gold-600 font-semibold group-hover:text-gold-700">
                Read Reviews on Google →
              </p>
            </a>

            {/* Facebook Reviews */}
            <a
              href="https://www.facebook.com/GMThibeau/reviews/?id=100064695380009&sk=reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
            >
              <div className="flex items-center justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-navy-900 mb-3 text-center">
                Facebook Reviews
              </h3>
              <p className="text-gray-600 text-center mb-4">
                Read testimonials from families we've helped reunite
              </p>
              <div className="flex items-center justify-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-center text-gold-600 font-semibold group-hover:text-gold-700">
                Read Reviews on Facebook →
              </p>
            </a>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Trusted by families throughout Ohio and beyond
            </p>
            <p className="text-sm text-gray-500 italic">
              Your success story could be next. Schedule a consultation today.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gold-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Immigration Journey?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Schedule a consultation today and let us help you navigate the path to your American dream with confidence and care.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-navy-900 px-10 py-6 text-xl font-bold rounded-xl shadow-2xl hover:shadow-gold-500/50 transition-all duration-300 hover:scale-105">
                Schedule Your Consultation
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
